import React, { useState, useEffect } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import ClosureMsgAutoCompleteTimeTextField from "./ClosureMsgAutoCompleteTimeTextField";
import dayjs from "../../../../../../utils/dayjs";
import type { TimeOption } from "../../../../../../utils/timeUtil";

const sanitizeTimeInput = (value: string): string =>
  value.match(/^\d{1,2}(?::\d{0,2})?(?:\s*[AaPp][Mm]?)?/)?.[0] ?? "";

interface Props {
  name?: string;
  startDate?: Date;
  endDate?: Date;
  startTimeValue?: string;
  endTimeValue?: string;
  startTimeOptions?: TimeOption[];
  endTimeOptions?: TimeOption[];
  timeZone?: string;
  handleStartTimeChange?: (value: string) => void;
  handleEndTimeChange?: (value: string) => void;
  disabled?: boolean;
}

const ClosureMsgAutoCompleteTime = ({
  name,
  startDate,
  endDate,
  startTimeValue,
  endTimeValue,
  startTimeOptions,
  endTimeOptions,
  timeZone,
  handleStartTimeChange,
  handleEndTimeChange,
  disabled = false,
}: Props) => {
  const [startOptions, setStartOptions] = useState<TimeOption[]>([]);
  const [endOptions, setEndOptions] = useState<TimeOption[]>([]);
  const [startInputValue, setStartInputValue] = useState("");
  const [endInputValue, setEndInputValue] = useState("");
  const [startTimeError, setStartTimeError] = useState(false);
  const [startTimeErrorMessage, setStartTimeErrorMessage] = useState("");
  const [endTimeError, setEndTimeError] = useState(false);
  const [endTimeErrorMessage, setEndTimeErrorMessage] = useState("");

  useEffect(() => {
    startTimeOptions &&
      startTimeOptions.length > 0 &&
      setStartOptions(startTimeOptions);
    endTimeOptions &&
      endTimeOptions.length > 0 &&
      setEndOptions(endTimeOptions);
  }, []);

  useEffect(() => {
    startTimeValue && setStartInputValue(startTimeValue);
    endTimeValue && setEndInputValue(endTimeValue);
    if (startTimeValue !== startInputValue) {
      setEndTimeErrorMessage("");
      setEndTimeError(false);
    }
  }, [startTimeValue, endTimeValue]);

  useEffect(() => {
    if (timeZone && startTimeValue && endInputValue) {
      handleEndDateAndTimeOnChange(
        startDate,
        endDate,
        startTimeValue,
        endTimeValue ?? "",
      );
    }
  }, [startDate, endDate, timeZone, endInputValue]);

  const handleEndDateAndTimeOnChange = (
    startDate: Date | undefined,
    endDate: Date | undefined,
    startTime: string,
    endTime: string,
  ) => {
    const compareTime = dayjs(startTime, "hh:mm A");
    const newTime = dayjs(endTime, "hh:mm A");
    const sDate = dayjs(startDate).format("MM/DD/YYYY");
    const eDate = dayjs(endDate).format("MM/DD/YYYY");

    const updatedEndDateTime = `${eDate} ${endTime}`;
    const selectedTimeZoneDateTime =
      timeZone && dayjs().tz(timeZone).format("MM/DD/YYYY hh:mm A");
    const isValidUpdatedEndDateTime = timeZone
      ? dayjs(updatedEndDateTime).isAfter(
          dayjs(selectedTimeZoneDateTime),
          "minute",
        )
      : false;
    const dateDiff = sDate === eDate ? 0 : 1;

    if (
      (newTime &&
        newTime.isValid() &&
        compareTime &&
        compareTime.isValid() &&
        isValidUpdatedEndDateTime &&
        newTime.isSameOrAfter(compareTime)) ||
      (newTime &&
        newTime.isValid() &&
        compareTime &&
        compareTime.isValid() &&
        isValidUpdatedEndDateTime &&
        dateDiff > 0)
    ) {
      setEndTimeErrorMessage("");
      setEndTimeError(false);
    } else {
      setEndTimeErrorMessage("Please enter a valid time");
      setEndTimeError(true);
    }
    setEndInputValue(endTime);
  };

  const handleStartTimeOnInputChange = (
    _event: React.SyntheticEvent,
    newValue: string,
  ) => {
    const filteredValue = sanitizeTimeInput(newValue);
    const compareTime = dayjs(endInputValue, "hh:mm A");
    const newTime = dayjs(filteredValue, "hh:mm A");
    const selectedTimeZoneDateTime = timeZone ? dayjs().tz(timeZone) : dayjs();
    const timeZoneEndTime = selectedTimeZoneDateTime
      .add(30, "minutes")
      .format("hh:mm A");
    if (newTime && newTime.isValid() && compareTime && compareTime.isValid()) {
      setStartTimeErrorMessage("");
      setStartTimeError(false);
    } else {
      setStartTimeErrorMessage("Please enter a valid time");
      setStartTimeError(true);
    }

    setStartInputValue(filteredValue);
    setEndInputValue(timeZoneEndTime);
    newTime.isValid() && handleStartTimeChange?.(filteredValue);
  };

  const handleEndTimeOnInputChange = (
    _event: React.SyntheticEvent,
    newValue: string,
  ) => {
    const filteredValue = sanitizeTimeInput(newValue);
    const newTime = dayjs(filteredValue, "hh:mm A");
    handleEndDateAndTimeOnChange(
      startDate,
      endDate,
      startInputValue,
      filteredValue,
    );
    newTime.isValid() && handleEndTimeChange?.(filteredValue);
  };

  return (
    <Autocomplete
      size="small"
      disableClearable
      freeSolo
      value={name === "Start Time" ? startInputValue : endInputValue}
      inputValue={name === "Start Time" ? startInputValue : endInputValue}
      onInputChange={
        name === "Start Time"
          ? handleStartTimeOnInputChange
          : handleEndTimeOnInputChange
      }
      sx={{
        "& .MuiInputBase-input": {
          height: "1rem",
        },
      }}
      disabled={disabled}
      options={
        name === "Start Time"
          ? startOptions && startOptions.map((option) => option.label)
          : endOptions && endOptions.map((option) => option.label)
      }
      renderInput={(params) => (
        <ClosureMsgAutoCompleteTimeTextField
          isError={name === "Start Time" ? startTimeError : endTimeError}
          errorMessage={
            name === "Start Time" ? startTimeErrorMessage : endTimeErrorMessage
          }
          params={params}
        />
      )}
      ListboxProps={{
        style: {
          maxHeight: 150,
          fontSize: 12,
        },
      }}
    />
  );
};

export default ClosureMsgAutoCompleteTime;
